import type {
  CornerRadiusModifierValue,
  OpacityModifierValue,
} from "schemas/generated/fileModifiers";

import React, { useState } from "react";

import TopLeftCorner from "@svg/modifiers/direction/top-left-corner";

import { Button } from "@replo/design-system/components/button/Button";
import { Field, FieldLabel } from "@replo/design-system/components/field/Field";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";

import CornerRadiusModifier from "./CornerRadiusModifier";
import OpacityModifier from "./OpacityModifier";

interface AppearanceModifierProps {
  onChange: (
    modifiers: (OpacityModifierValue | CornerRadiusModifierValue)[],
  ) => void;
}

const AppearanceModifier: React.FC<AppearanceModifierProps> = ({
  onChange,
}) => {
  const [isIndividualCorners, setIsIndividualCorners] = useState(false);

  const handleOpacityChange = React.useCallback(
    (modifier: OpacityModifierValue) => {
      onChange([modifier]);
    },
    [onChange],
  );

  const handleCornerRadiusChange = React.useCallback(
    (modifiers: CornerRadiusModifierValue[]) => {
      onChange(modifiers);
    },
    [onChange],
  );

  const handleCornerModeChange = React.useCallback((isIndividual: boolean) => {
    setIsIndividualCorners(isIndividual);
  }, []);

  const handleCornerModeToggle = React.useCallback(() => {
    handleCornerModeChange(!isIndividualCorners);
  }, [isIndividualCorners, handleCornerModeChange]);

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[1fr_1fr_min-content] gap-2 items-end pt-1">
        <Field size="xs" className="h-full">
          <FieldLabel size="xs" htmlFor="opacity" UNSAFE_className="text-muted">
            Opacity
          </FieldLabel>
          <OpacityModifier onChange={handleOpacityChange} />
        </Field>
        <Field size="xs" className="h-full">
          <FieldLabel
            size="xs"
            htmlFor="corner-radius"
            UNSAFE_className="text-muted"
          >
            Corner Radius
          </FieldLabel>
          <CornerRadiusModifier
            onChange={handleCornerRadiusChange}
            onModeChange={handleCornerModeChange}
            showButton={false}
            mode={isIndividualCorners ? "individual" : "unified"}
            showIndividualInputs={isIndividualCorners}
          />
        </Field>
        <Tooltip
          content={
            isIndividualCorners
              ? "Set unified corners"
              : "Set individual corners"
          }
        >
          <Button
            icon
            variant="secondary"
            size="sm"
            onClick={handleCornerModeToggle}
          >
            <TopLeftCorner />
          </Button>
        </Tooltip>
      </div>
      {isIndividualCorners && (
        <div className="grid grid-cols-[1fr_min-content] gap-8">
          <div>
            <CornerRadiusModifier
              onChange={handleCornerRadiusChange}
              onModeChange={handleCornerModeChange}
              showButton={false}
              mode="individual"
              onlyShowIndividualInputs={true}
            />
          </div>
          <div></div>
        </div>
      )}
    </div>
  );
};

export default AppearanceModifier;
